import React, { useState, useContext, useEffect, useRef } from "react";
import "./login.css";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import AuthContext from "../../contexts/AuthContext";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { FiMail, FiLock } from "react-icons/fi";
import SplashCursor from "../../components/SplashCursor";

// Covers its eyes with its paws while the password is visible; otherwise its
// pupils track the cursor. `pupilOffset` (small {x,y} in SVG viewBox units)
// shifts just the pupil/sparkle within the static white of the eye.
const PandaMascot = React.forwardRef(({ isHidingEyes, pupilOffset }, ref) => (
  <div className="panda-mascot" aria-hidden="true" ref={ref}>
    <svg viewBox="0 0 120 120" width="92" height="92">
      {/* Ears — two-tone for a softer, kawaii look */}
      <circle cx="24" cy="24" r="18" fill="#22252b" />
      <circle cx="96" cy="24" r="18" fill="#22252b" />
      <circle cx="24" cy="25" r="8" fill="#3a3d44" />
      <circle cx="96" cy="25" r="8" fill="#3a3d44" />

      {/* Head */}
      <circle cx="60" cy="64" r="48" fill="#ffffff" stroke="#e4e7ee" strokeWidth="2" />

      {isHidingEyes ? (
        <g className="panda-paws">
          <ellipse cx="37" cy="58" rx="17" ry="15" fill="#3a3d44" stroke="#1c1e22" strokeWidth="1.5" />
          <circle cx="30" cy="54" r="2.6" fill="#9aa0ad" />
          <circle cx="38" cy="50" r="2.6" fill="#9aa0ad" />
          <circle cx="45" cy="55" r="2.6" fill="#9aa0ad" />
          <ellipse cx="83" cy="58" rx="17" ry="15" fill="#3a3d44" stroke="#1c1e22" strokeWidth="1.5" />
          <circle cx="75" cy="55" r="2.6" fill="#9aa0ad" />
          <circle cx="82" cy="50" r="2.6" fill="#9aa0ad" />
          <circle cx="90" cy="54" r="2.6" fill="#9aa0ad" />
        </g>
      ) : (
        <g className="panda-eyes">
          <ellipse cx="39" cy="56" rx="13" ry="16" fill="#22252b" transform="rotate(-15 39 56)" />
          <ellipse cx="81" cy="56" rx="13" ry="16" fill="#22252b" transform="rotate(15 81 56)" />
          <circle cx="41" cy="57" r="5.5" fill="#fff" />
          <circle cx="79" cy="57" r="5.5" fill="#fff" />
          <g className="panda-pupil" style={{ transform: `translate(${pupilOffset.x}px, ${pupilOffset.y}px)` }}>
            <circle cx="41" cy="57" r="2.6" fill="#22252b" />
            <circle cx="43.5" cy="54.5" r="2" fill="#fff" opacity="0.9" />
          </g>
          <g className="panda-pupil" style={{ transform: `translate(${pupilOffset.x}px, ${pupilOffset.y}px)` }}>
            <circle cx="79" cy="57" r="2.6" fill="#22252b" />
            <circle cx="76.5" cy="54.5" r="2" fill="#fff" opacity="0.9" />
          </g>
        </g>
      )}

      {/* Nose + smile */}
      <ellipse cx="60" cy="80" rx="5.5" ry="4" fill="#22252b" />
      <path d="M60 84 Q60 91 51 90" stroke="#22252b" strokeWidth="2.2" fill="none" strokeLinecap="round" />
      <path d="M60 84 Q60 91 69 90" stroke="#22252b" strokeWidth="2.2" fill="none" strokeLinecap="round" />

      {/* Blush */}
      <ellipse cx="24" cy="76" rx="7" ry="4.5" fill="#ffb3c1" opacity="0.7" />
      <ellipse cx="96" cy="76" rx="7" ry="4.5" fill="#ffb3c1" opacity="0.7" />
    </svg>
  </div>
));

const Login = () => {
  const navigate = useNavigate();
  const { loginUser, loading } = useContext(AuthContext);

  const [emailAddress, setEmailAddress] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [validationErrors, setValidationErrors] = useState({
    emailAddress: "",
    password: "",
  });

  const pandaRef = useRef(null);
  const [pupilOffset, setPupilOffset] = useState({ x: 0, y: 0 });

  // Pupils lean toward the cursor, reaching full deflection ~60px away from
  // the panda so they don't jitter when the mouse is right on top of it.
  useEffect(() => {
    const maxOffset = 2.5;
    let frame = null;

    const handleMouseMove = (e) => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        const el = pandaRef.current;
        if (!el) return;

        const rect = el.getBoundingClientRect();
        const dx = e.clientX - (rect.left + rect.width / 2);
        const dy = e.clientY - (rect.top + rect.height / 2);
        const distance = Math.hypot(dx, dy);
        const factor = distance === 0 ? 0 : Math.min(1, distance / 60);
        const angle = Math.atan2(dy, dx);

        setPupilOffset({
          x: Math.cos(angle) * maxOffset * factor,
          y: Math.sin(angle) * maxOffset * factor,
        });
      });
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const validateFields = () => {
    const errors = {};
    if (!emailAddress.trim()) {
      errors.emailAddress = "Email is required.";
    } else if (!/\S+@\S+\.\S+/.test(emailAddress)) {
      errors.emailAddress = "Invalid email format.";
    }

    if (!password.trim()) {
      errors.password = "Password is required.";
    } else if (password.length < 6) {
      errors.password = "Password must be at least 6 characters.";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateFields()) {
      toast.error("Please fill in all required fields.", {
        position: "top-right",
        autoClose: 1000,
      });
      return;
    }

    try {
      const response = await loginUser({ emailAddress, password });

      if (response && (response.status === "Failed" || response.code === 404)) {
        toast.error(response.message || "Invalid credentials", {
          position: "top-right",
          autoClose: 2000,
          theme: "colored",
        });
        return;
      }

      toast.success("Login successful!", {
        position: "top-right",
        autoClose: 1000,
      });

      navigate("/");
    } catch (error) {
      toast.error(`Login failed: ${error.message || "Unexpected error"}`, {
        position: "top-right",
        autoClose: 1000,
      });
    }
  };

  return (
    <main className="login-premium">
      <div className="login-premium-bg" aria-hidden="true">
        <video
          className="login-bg-video"
          autoPlay
          loop
          muted
          playsInline
          poster="/images/hero-desktop-poster.webp"
        >
          <source src="/images/hero-video.webm" type="video/webm" />
          <source src="/images/hero-video.mp4" type="video/mp4" />
        </video>
      </div>

      <SplashCursor
        COLOR="#3d0ee7b0"
        RAINBOW_MODE={false}
        Z_INDEX={0}
        EXCLUDE_SELECTOR=".login-card"
      />

      <div className="login-card">
        {/* Panda covers its eyes once the password is actually visible on screen —
            not while it's masked as dots. */}
        <PandaMascot ref={pandaRef} isHidingEyes={showPassword} pupilOffset={pupilOffset} />

        <h1 className="login-card-title">Hello!</h1>
        <p className="login-card-subtitle">Sign in to your account</p>

        <form onSubmit={handleSubmit} autoComplete="off">
          {/* Email Field */}
          <div className="login-field-group">
            <label>
              <span className="text-danger">* </span>Email
            </label>
            <div
              className={`login-input-wrapper ${validationErrors.emailAddress ? "is-invalid" : ""}`}
            >
              <FiMail className="login-input-icon" />
              <input
                type="email"
                placeholder="Email"
                value={emailAddress}
                // Forces lowercase and disables suggestions
                onChange={(e) => setEmailAddress(e.target.value.toLowerCase())}
                autoComplete="one-time-code"
                autoCorrect="off"
                spellCheck="false"
              />
            </div>
            {validationErrors.emailAddress && (
              <div className="login-error-text">{validationErrors.emailAddress}</div>
            )}
          </div>

          {/* Password Field */}
          <div className="login-field-group">
            <label>
              <span className="text-danger">* </span>Password
            </label>
            <div
              className={`login-input-wrapper ${validationErrors.password ? "is-invalid" : ""}`}
            >
              <FiLock className="login-input-icon" />
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                // Disables suggestions for password
                autoComplete="new-password"
                autoCorrect="off"
              />
              <button
                type="button"
                className="login-password-toggle"
                onClick={togglePasswordVisibility}
                tabIndex="-1"
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
            {validationErrors.password && (
              <div className="login-error-text">{validationErrors.password}</div>
            )}
          </div>

          <button type="submit" disabled={loading} className="login-submit-btn">
            {loading ? "Logging in..." : "Login"}
          </button>
        </form>
      </div>
    </main>
  );
};

export default Login;
